/**
 * Per-action column-reference resolver.
 *
 * Replaces the previous static spec-rewriting passes (`extractInlineExpressions`,
 * `placeSyntheticDerives`, upfront `materializeDottedPaths`) with one
 * per-action primitive: walk the action's column-ref sites against the
 * *live* table and resolve each ref by augmenting the table with the
 * needed column.
 *
 * For every column-ref site (filter.where leaves, sort.keys[].column,
 * rollup.columns + rollup.aggregations[].column, pivot.{key,value}Column,
 * fold.columns, spread.column, unroll.column, expandTimeseries.{columns,
 * splitBy}) the resolver classifies the ref as one of:
 *
 *   - **literal column on the live table** — keep, no change.
 *   - **math.js expression with operators / functions** (e.g. `(min == 0
 *     ? 1 : 0)`, `value(last(datapoints(rate)))`) — evaluate as a derive
 *     against the live table, append the result as a synthetic
 *     `__expr_<hash>` column, rewrite the ref to that name.
 *   - **dotted path whose root column exists on the live table** (e.g.
 *     `well_ref.externalId` after `derive well_ref = well`) — materialize
 *     the dotted path as a flat column on the live table; the ref stays
 *     as the dotted-path string (which is now a flat column name).
 *   - **none of the above** — throw {@link ColumnNotFoundError} listing the
 *     live table's column names.
 *
 * Why per-action and not static: the previous architecture tried to
 * predict what columns would exist when an action runs by combining input
 * columns with `collectStaticOutputNames(spec)`. That works for actions
 * whose ref-sites point to input columns or earlier static outputs, but
 * not for refs to operator-produced columns whose roots only exist after
 * the producing operator runs (e.g. an inline expression in a rollup
 * referencing an earlier rollup's aggregation output, or a dotted path
 * traversing a derive output's nested object). Resolving per-action
 * against the live table eliminates that whole class of failure mode and
 * removes the special cases the static rewriter accumulated.
 */
import type { ColumnTable } from 'arquero';
import * as aq from 'arquero';

import { collectColumnRefs, mapColumnRefs } from './column-refs-walker.ts';
import { evaluateDerivedFields } from './derived-fields.ts';
import { isExpressionAst, SYNTHETIC_DERIVE_PREFIX, stableHash } from './inline-expressions.ts';
import { dottedPathParent, isDottedPath, materializeDottedPaths } from './nested-paths.ts';
import type { Action, PipelineSpec } from './pipeline-schema.ts';
import type { PrefetchCtx } from './timeseries-prefetch.ts';
import { ColumnNotFoundError } from '#plugin-chat/lib/tool/errors/index.ts';

export interface PrepareActionResult {
  /** Table augmented with synthetic-expression columns and materialised dotted paths. */
  table: ColumnTable;
  /** Action with column refs rewritten to point at resolved column names. */
  action: Action;
  /** Warnings collected from inline-expression evaluation, propagated to the pipeline. */
  warnings: readonly string[];
}

/**
 * Resolve every column-ref site of `action` against `table` and return
 * an augmented table + rewritten action ready for {@link runAction}.
 */
export async function prepareTableForAction(
  action: Action,
  table: ColumnTable,
  ctx?: PrefetchCtx,
): Promise<PrepareActionResult> {
  const liveColumns = new Set(table.columnNames());
  const wrappedSpec: PipelineSpec = { actions: [action] };
  const refs = collectColumnRefs(wrappedSpec);

  const expressionRefs = new Set<string>();
  const dottedRefs = new Set<string>();
  const unknownRefs: string[] = [];
  for (const ref of refs) {
    if (liveColumns.has(ref)) {
      continue;
    }
    if (isExpressionAst(ref)) {
      expressionRefs.add(ref);
      continue;
    }
    if (isDottedPath(ref) && liveColumns.has(dottedPathParent(ref))) {
      dottedRefs.add(ref);
      continue;
    }
    unknownRefs.push(ref);
  }

  if (unknownRefs.length > 0) {
    // Dedup unknowns by ref text — agents repeating the same typo across
    // sites should see one entry, not N.
    const dedupedMissing = [...new Set(unknownRefs)];
    throw new ColumnNotFoundError(action.type, dedupedMissing, [...liveColumns].sort());
  }

  // Pre-compute the ref → resolved-name mapping. Expression refs map to
  // synthetic `__expr_<hash>` names; dotted refs map to themselves (the
  // dotted-path string becomes the flat column name after materialisation).
  const refToName = new Map<string, string>();
  for (const expr of expressionRefs) {
    refToName.set(expr, `${SYNTHETIC_DERIVE_PREFIX}${stableHash(expr)}`);
  }

  let augmentedTable = table;
  const warnings: string[] = [];

  if (refToName.size > 0) {
    const derives = [...refToName].map(([expression, name]) => ({ name, expression }));
    const data = augmentedTable.objects() as Record<string, unknown>[];
    const result = await evaluateDerivedFields(data, derives, { ctx });
    augmentedTable = aq.from(result.data);
    warnings.push(...result.warnings);
  }

  if (dottedRefs.size > 0) {
    augmentedTable = materializeDottedPaths(augmentedTable, [...dottedRefs]);
  }

  const rewrittenSpec = mapColumnRefs(wrappedSpec, (ref) => refToName.get(ref) ?? ref);
  // The wrapper had exactly one action — the rewritten spec has the same
  // shape, with column refs rewritten in place.
  const rewrittenAction = rewrittenSpec.actions[0];
  if (rewrittenAction === undefined) {
    // Should be unreachable given mapColumnRefs preserves the actions array
    // length; satisfying the type checker without an unsafe cast.
    return { table: augmentedTable, action, warnings };
  }

  return { table: augmentedTable, action: rewrittenAction, warnings };
}
